import React, { Component, ReactNode } from "react";
import { SeverityLevel } from "@microsoft/applicationinsights-common";
import { v4 as uuidv4 } from "uuid";
import { AppInsightsService } from "../services/AppInsightsService";
import ErrorFallback from "./ErrorFallback";

interface ErrorBoundaryProps {
    children: ReactNode;
    resetKey?: string;
    pageName?: string;
}

interface ErrorBoundaryState {
    hasError: boolean;
    correlationId?: string;
    message?: string;
    url?: string;
    timestamp?: string;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    private hasSentError: boolean = false;

    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        if (this.hasSentError) return;

        const isAppInsightsEnabled = !!AppInsightsService.appInsights;
        const correlationId = isAppInsightsEnabled ? uuidv4() : "Something went wrong, please contact your customer support";
        const url = window.location.href;
        const message = error.message;
        const pageInfo = this.props.pageName ? `Unhandled Error in [ ${this.props.pageName} ]` : "Unhandled Error";

        let timestamp: string | undefined;
        if (isAppInsightsEnabled) {
            try {
                const storedLocale = localStorage.getItem("userLanguagePreferences");
                const userLocale = storedLocale ? JSON.parse(storedLocale) : "en-GB";
                timestamp = new Date().toLocaleString(userLocale, {
                    hour12: true,
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit"
                });
            } catch (e) {
                console.warn("Could not read language from Preferences", e);
            }
        }

        this.hasSentError = true;

        console.error("[Unhandled Error in Tab]", {
            correlationId,
            message,
            url,
            pageInfo,
            stack: errorInfo.componentStack
        });

        if (isAppInsightsEnabled) {
            AppInsightsService.trackException(
                error,
                {severityLevel: SeverityLevel.Error,
                properties: {
                    correlationId,
                    url,
                    componentStack: errorInfo.componentStack,
                    pageInfo
                },
            });
        }

        this.setState({
            hasError: true,
            correlationId,
            message,
            url,
            timestamp,
        });
    }

    render() {
        if (this.state.hasError) {
            return (
                <ErrorFallback
                    correlationId={this.state.correlationId}
                    message={this.state.message}
                    timestamp={this.state.timestamp}
                />
            );
        }

        return this.props.children;
    }
}
